import { useState, useEffect, useCallback } from 'react';
import { getPaymentRecovery } from '../lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { CurrencyInr, Warning, CheckCircle, Clock, Phone, MagnifyingGlass, ArrowDown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(v || 0);

export default function PaymentRecoveryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPaymentRecovery();
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load recovery data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return null;

  const statusBadge = (s) => {
    if (s === 'paid') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded"><CheckCircle size={12} weight="fill" /> Paid</span>;
    if (s === 'partial') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded"><Clock size={12} weight="fill" /> Partial</span>;
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded"><Warning size={12} weight="fill" /> Pending</span>;
  };

  // Filter
  const filtered = data.distributors.filter(d => {
    if (search) {
      const q = search.toLowerCase();
      const match = d.distributor_name.toLowerCase().includes(q) ||
        d.agencies.some(a => a.agency_name.toLowerCase().includes(q));
      if (!match) return false;
    }
    if (filterStatus !== 'all') {
      if (filterStatus === 'pending') return d.total_pending > 0;
      if (filterStatus === 'paid') return d.total_pending <= 0;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="payment-recovery-page">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Payment Recovery</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="summary-total-bookings">
          <p className="text-[10px] text-blue-600 uppercase font-semibold">Total Booking Amount</p>
          <p className="text-2xl font-bold text-blue-700 font-mono">{fmt(data.summary.total_booking_amount)}</p>
        </div>
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="summary-total-deposits">
          <p className="text-[10px] text-green-600 uppercase font-semibold">Total Deposits Received</p>
          <p className="text-2xl font-bold text-green-700 font-mono">{fmt(data.summary.total_deposits)}</p>
        </div>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="summary-total-pending">
          <p className="text-[10px] text-red-600 uppercase font-semibold">Total Pending Recovery</p>
          <p className="text-2xl font-bold text-red-700 font-mono">{fmt(data.summary.total_pending)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search distributor / agency..."
          matchCount={filtered.length}
          totalCount={(data?.distributors || []).length}
          noun="distributors"
          testId="recovery-search"
          inputClassName="pl-8 pr-7 h-9 text-sm"
        />
        <div className="flex gap-1">
          {['all', 'pending', 'paid'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-all ${filterStatus === s ? 'bg-[#0C1D3F] text-white border-[#0C1D3F]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`} data-testid={`filter-${s}`}>
              {s === 'all' ? 'All' : s === 'pending' ? 'Pending' : 'Paid'}
            </button>
          ))}
        </div>
      </div>

      {/* Per-Distributor Sections */}
      {filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No distributors found</div>
      ) : filtered.map(dist => (
        <div key={dist.distributor_id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`dist-recovery-${dist.distributor_id}`}>
          {/* Distributor Header */}
          <div className={`px-4 py-3 flex items-center justify-between ${dist.total_pending > 0 ? 'bg-red-50 border-b border-red-100' : 'bg-green-50 border-b border-green-100'}`}>
            <div>
              <h3 className="font-semibold text-gray-900">{dist.distributor_name}</h3>
              <p className="text-xs text-gray-500">{dist.distributor_email} {dist.distributor_mobile && <span>| <Phone size={10} className="inline" /> {dist.distributor_mobile}</span>}</p>
            </div>
            <div className="text-right">
              <p className={`text-lg font-bold font-mono ${dist.total_pending > 0 ? 'text-red-700' : 'text-green-700'}`}>
                {dist.total_pending > 0 ? `${fmt(dist.total_pending)} Pending` : 'All Paid'}
              </p>
            </div>
          </div>

          {/* Agency Table */}
          <Table className="table-dense">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Agency</TableHead>
                <TableHead>Oil Co.</TableHead>
                <TableHead className="text-right">Bookings</TableHead>
                <TableHead className="text-right">Cylinders</TableHead>
                <TableHead className="text-right">Booking Amt</TableHead>
                <TableHead className="text-right">Deposits</TableHead>
                <TableHead className="text-right">Pending</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Booking</TableHead>
                <TableHead>Last Deposit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dist.agencies.map(a => (
                <TableRow key={a.agency_id} className={a.pending > 0 ? 'bg-red-50/40' : ''}>
                  <TableCell className="font-medium text-sm">{a.agency_name}</TableCell>
                  <TableCell><span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{a.oil_company || '-'}</span></TableCell>
                  <TableCell className="text-right font-mono text-sm">{a.booking_count}</TableCell>
                  <TableCell className="text-right font-mono font-bold text-sm">{a.total_cylinders}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-blue-600">{fmt(a.total_bookings)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-green-600">{fmt(a.total_deposits)}</TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono font-bold text-sm ${a.pending > 0 ? 'text-red-700' : 'text-green-700'}`}>
                      {a.pending > 0 ? fmt(a.pending) : 'Paid'}
                    </span>
                  </TableCell>
                  <TableCell>{statusBadge(a.status)}</TableCell>
                  <TableCell className="text-xs text-gray-500">{a.last_booking_date || '-'}</TableCell>
                  <TableCell className="text-xs text-gray-500">{a.last_deposit_date || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}
